import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { OperatorAppVersionRequirementsService } from '../../../api/api-services/operator-app-version-requirements.service';
import { IOperatorAppVersionRequirementsVm } from '../../../api/operator-app-version-requirements-vm.model';
import { IPlatformVersionRequirementVm } from '../../../api/platform-version-requirement-vm.model';

type PlatformKey = 'android' | 'ios';

interface IPlatformForm {
  key: PlatformKey;
  label: string;
  icon: string;
  example: string;
  minimumVersion: string;
  recommendedVersion: string;
  storeUrl: string;
}

const VERSION_PATTERN = /^\d{1,6}(\.\d{1,6}){0,3}$/;

/** Compares dotted version names numerically ("1.10" > "1.9"); missing parts count as 0. */
function compareVersions(left: string, right: string): number {
  const leftParts = left.split('.').map(Number);
  const rightParts = right.split('.').map(Number);
  for (let i = 0; i < Math.max(leftParts.length, rightParts.length); i++) {
    const difference = (leftParts[i] ?? 0) - (rightParts[i] ?? 0);
    if (difference !== 0) return Math.sign(difference);
  }
  return 0;
}

/**
 * Minimum (forced) and recommended app versions per platform. The mobile apps read them on every
 * start and when they return to the foreground.
 */
@Component({
  selector: 'app-app-versions',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, ProgressSpinnerModule],
  templateUrl: './app-versions.component.html',
  styleUrl: './app-versions.component.scss',
})
export class AppVersionsComponent implements OnInit {
  private _service = inject(OperatorAppVersionRequirementsService);

  loading = false;
  saving = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  saved: IOperatorAppVersionRequirementsVm | null = null;
  platforms: IPlatformForm[] = [];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = null;
    this._service.get().subscribe({
      next: (requirements) => {
        this._apply(requirements);
        this.loading = false;
      },
      error: (e) => {
        this.loading = false;
        this.errorMessage = e?.error?.message ?? 'Nie udało się pobrać ustawień wersji.';
      },
    });
  }

  save(): void {
    this.errorMessage = null;
    this.successMessage = null;

    const validationError = this._validate();
    if (validationError) {
      this.errorMessage = validationError;
      return;
    }

    const raisedMinimums = this.platforms.filter((platform) => this._minimumRaised(platform));
    if (raisedMinimums.length) {
      const list = raisedMinimums.map((platform) => `${platform.label} poniżej ${platform.minimumVersion.trim()}`).join(', ');
      const confirmed = confirm(
        `Użytkownicy z wersją ${list} zostaną zablokowani, dopóki nie zaktualizują aplikacji.\n\n` +
          'Upewnij się, że ta wersja jest już dostępna dla wszystkich w sklepie (wdrożenie 100% w Google Play, ' +
          'wersja zatwierdzona i wydana w App Store). Zapisać?',
      );
      if (!confirmed) return;
    }

    this.saving = true;
    this._service
      .put({
        android: this._toVm(this._platform('android')),
        ios: this._toVm(this._platform('ios')),
      })
      .subscribe({
        next: (requirements) => {
          this._apply(requirements);
          this.saving = false;
          this.successMessage =
            'Zapisano. Aplikacje pobiorą nowe ustawienia przy najbliższym uruchomieniu lub powrocie do aplikacji.';
        },
        error: (e) => {
          this.saving = false;
          this.errorMessage = e?.error?.message ?? 'Nie udało się zapisać ustawień wersji.';
        },
      });
  }

  private _apply(requirements: IOperatorAppVersionRequirementsVm): void {
    this.saved = requirements;
    this.platforms = [
      this._toForm('android', 'Android', 'pi pi-android', '1.23', requirements.android),
      this._toForm('ios', 'iOS', 'pi pi-apple', '1.12', requirements.ios),
    ];
  }

  private _toForm(
    key: PlatformKey,
    label: string,
    icon: string,
    example: string,
    requirement: IPlatformVersionRequirementVm,
  ): IPlatformForm {
    return {
      key,
      label,
      icon,
      example,
      minimumVersion: requirement.minimumVersion ?? '',
      recommendedVersion: requirement.recommendedVersion ?? '',
      storeUrl: requirement.storeUrl ?? '',
    };
  }

  private _toVm(platform: IPlatformForm): IPlatformVersionRequirementVm {
    return {
      minimumVersion: platform.minimumVersion.trim() || null,
      recommendedVersion: platform.recommendedVersion.trim() || null,
      storeUrl: platform.storeUrl.trim(),
    };
  }

  private _platform(key: PlatformKey): IPlatformForm {
    return this.platforms.find((platform) => platform.key === key)!;
  }

  private _validate(): string | null {
    for (const platform of this.platforms) {
      const minimum = platform.minimumVersion.trim();
      const recommended = platform.recommendedVersion.trim();
      if (minimum && !VERSION_PATTERN.test(minimum)) {
        return `${platform.label}: wersja minimalna „${minimum}” ma zły format — użyj np. ${platform.example}.`;
      }
      if (recommended && !VERSION_PATTERN.test(recommended)) {
        return `${platform.label}: wersja zalecana „${recommended}” ma zły format — użyj np. ${platform.example}.`;
      }
      if (minimum && recommended && compareVersions(recommended, minimum) < 0) {
        return `${platform.label}: wersja zalecana nie może być niższa niż minimalna.`;
      }
    }
    return null;
  }

  private _minimumRaised(platform: IPlatformForm): boolean {
    const minimum = platform.minimumVersion.trim();
    const previous = this.saved?.[platform.key]?.minimumVersion;
    return !!minimum && VERSION_PATTERN.test(minimum) && (!previous || compareVersions(minimum, previous) > 0);
  }
}
